/**
 * Cookie情報の取得・更新に関する共通定義
 */
import _Vue from 'vue'

// cookie定義型の取り込み
import { AppCookieTypes } from '@/models/AppCookieTypes'

// デフォルト値として使うコード類の取り込み
import { SubjectCode } from '@/const/SubjectCode'
import { GradeCode } from '@/const/GradeCode'

// エラー定義の取り込み
import {
  StudentAuthAppError,
  TeacherAuthAppError,
  AcademyAuthAppError,
  AdminAuthAppError,
} from '@/models/AppErrorClasses'

/**
 * cookie関連メソッドの標準型定義
 */
interface CookieFunctions<T> {
  /**
   * cookie取得
   *
   * @returns cookie情報
   */
  get: () => T | null
  /**
   * cookie登録
   *
   * @param value cookie情報
   */
  set: (value: T) => Promise<void>
  /**
   * cookie削除
   */
  remove: () => void
}

export default {
  /**
   * plugin登録時に実行される処理
   *
   * @param Vue Vueのコンストラクタ
   */
  install(Vue: typeof _Vue): void {
    /**
     * 認証情報
     */
    const auth = createCookieFunctions<AppCookieTypes.Auth>(Vue, 'auth')

    /**
     * 生徒情報
     *   取得は独自処理が必要なので、共通定義をラップして使う
     */
    const authStudentFunctions = createCookieFunctions<AppCookieTypes.AuthStudent>(Vue, 'authStudent')

    /**
     * 生徒情報取得
     *   未設定の場合は例外が発生する
     *
     * @returns 生徒情報取得
     */
    const getAuthStudent = function (): AppCookieTypes.AuthStudent {
      const student = authStudentFunctions.get()
      // 未設定の場合は例外発生(共通処理で画面遷移制御)
      if (!student) {
        throw new StudentAuthAppError()
      }
      return student
    }
    const authStudent = { get: getAuthStudent, set: authStudentFunctions.set, remove: authStudentFunctions.remove }

    /**
     * (教室)先生情報
     *   取得は独自処理が必要なので、共通定義をラップして使う
     */
    const authTeacherFunctions = createCookieFunctions<AppCookieTypes.AuthTeacher>(Vue, 'authTeacher')

    /**
     * 先生情報取得
     *   未設定の場合は例外が発生する
     *
     * @returns 先生情報取得
     */
    const getAuthTeacher = function (): AppCookieTypes.AuthTeacher {
      const teacher = authTeacherFunctions.get()
      // 未設定の場合は例外発生(共通処理で画面遷移制御)
      if (!teacher) {
        throw new TeacherAuthAppError()
      }
      return teacher
    }
    const authTeacher = { get: getAuthTeacher, set: authTeacherFunctions.set, remove: authTeacherFunctions.remove }

    /**
     * 塾管理者情報
     *   取得は独自処理が必要なので、共通定義をラップして使う
     */
    const authAcademyFunctions = createCookieFunctions<AppCookieTypes.AuthAcademy>(Vue, 'authAcademy')

    /**
     * 塾管理者情報取得
     *   未設定の場合は例外が発生する
     *
     * @returns 塾管理者情報取得
     */
    const getAuthAcademy = function (): AppCookieTypes.AuthAcademy {
      const academy = authAcademyFunctions.get()
      // 未設定の場合は例外発生(共通処理で画面遷移制御)
      if (!academy) {
        throw new AcademyAuthAppError()
      }
      return academy
    }
    const authAcademy = { get: getAuthAcademy, set: authAcademyFunctions.set, remove: authAcademyFunctions.remove }

    /**
     * システム管理者情報
     *   取得は独自処理が必要なので、共通定義をラップして使う
     */
    const authAdminFunctions = createCookieFunctions<AppCookieTypes.AuthAdmin>(Vue, 'authAdmin')

    /**
     * システム管理者情報取得
     *   未設定の場合は例外が発生する
     *
     * @returns 塾管理者情報取得
     */
    const getAuthAdmin = function (): AppCookieTypes.AuthAdmin {
      const admin = authAdminFunctions.get()
      // 未設定の場合は例外発生(共通処理で画面遷移制御)
      if (!admin) {
        throw new AdminAuthAppError()
      }
      return admin
    }
    const authAdmin = { get: getAuthAdmin, set: authAdminFunctions.set, remove: authAdminFunctions.remove }

    /**
     * 生徒メニューでの選択情報-教科コード
     *   取得は独自処理が必要なので、共通定義をラップして使う
     */
    const subjectCodeFunctions = createCookieFunctions<AppCookieTypes.StudentMenuState.SubjectCode>(
      Vue,
      'studentMenuState_subjectCode',
      true
    )

    /**
     * 教科コード取得
     *   未設定の場合はデフォルト値を設定してから返す
     *
     * @returns 教科コード
     */
    const getSubjectCode = async function (): Promise<AppCookieTypes.StudentMenuState.SubjectCode> {
      let code = subjectCodeFunctions.get()
      // 未設定の場合はデフォルト値を設定
      if (!code) {
        code = SubjectCode.LIST[0].code
        await subjectCodeFunctions.set(code)
      }
      return code
    }
    const subjectCode = { get: getSubjectCode, set: subjectCodeFunctions.set, remove: subjectCodeFunctions.remove }

    /**
     * 生徒メニューでの選択情報-学年コード
     *   取得は独自処理が必要なので、共通定義をラップして使う
     */
    const gradeCodeFunctions = createCookieFunctions<AppCookieTypes.StudentMenuState.GradeCode>(
      Vue,
      'studentMenuState_gradeCode',
      true
    )

    /**
     * 学年コード取得
     *   未設定の場合はデフォルト値を設定してから返す
     *
     * @returns 学年コード
     */
    const getGradeCode = async function (): Promise<AppCookieTypes.StudentMenuState.GradeCode> {
      let code = gradeCodeFunctions.get()
      // 未設定の場合はデフォルト値を設定
      if (!code) {
        // ログイン時の生徒情報から学年コードを取得
        code = authStudent.get().gradeCode
        // N_LMS-395 初期リリース段階での教科・学年の制限に伴う学年コードチェック
        if (!GradeCode.findByCode(code)) {
          // 生徒の学年がフロント側未定義の場合はデフォルト値を設定
          code = GradeCode.LIST[0].code
        }
        await gradeCodeFunctions.set(code)
      }
      return code
    }
    const gradeCode = { get: getGradeCode, set: gradeCodeFunctions.set, remove: gradeCodeFunctions.remove }

    // 生徒メニューの選択情報設定
    const studentMenuState = { subjectCode: subjectCode, gradeCode: gradeCode }

    /**
     * 全Cookieの有効期間延長
     */
    const updateCookieExpire = async function (): Promise<void> {
      // 認証情報の更新
      const tmpAuth = auth.get()
      if (tmpAuth) {
        await auth.set(tmpAuth)
      }
      // 生徒情報の更新(エラーを発生させないため、ラップ前の共通処理を使用する)
      const tmpAuthStudent = authStudentFunctions.get()
      if (tmpAuthStudent) {
        await authStudent.set(tmpAuthStudent)
      }
      // 先生情報の更新(エラーを発生させないため、ラップ前の共通処理を使用する)
      const tmpAuthTeacher = authTeacherFunctions.get()
      if (tmpAuthTeacher) {
        await authTeacher.set(tmpAuthTeacher)
      }
      // 塾管理者情報の更新(エラーを発生させないため、ラップ前の共通処理を使用する)
      const tmpAuthAcademy = authAcademyFunctions.get()
      if (tmpAuthAcademy) {
        await authAcademy.set(tmpAuthAcademy)
      }
      // システム管理者情報の更新(エラーを発生させないため、ラップ前の共通処理を使用する)
      const tmpAuthAdmin = authAdminFunctions.get()
      if (tmpAuthAdmin) {
        await authAdmin.set(tmpAuthAdmin)
      }
    }

    /**
     * 認証情報系Cookieの削除
     */
    const removeAuth = function (): void {
      // 認証情報の削除
      auth.remove()
      // 生徒情報の削除
      authStudent.remove()
      // 先生情報の削除
      authTeacher.remove()
      // 塾管理者情報の削除
      authAcademy.remove()
      // システム管理者情報の削除
      authAdmin.remove()
    }

    Vue.prototype.$appCookies = {
      auth,
      authStudent,
      authTeacher,
      authAcademy,
      authAdmin,
      studentMenuState,
      updateCookieExpire,
      removeAuth,
    }
  },
}

/**
 * cookie関連メソッドの一括生成
 *   標準的な処理の場合はこれを使用、独自処理を挟む場合は個別定義する
 *
 * @param Vue Vueのコンストラクタ
 * @param key cookie名
 * @param isSessionCookie セッションクッキーにするか否か(省略した場合は通常のcookie)
 * @returns cookie関連メソッド
 */
function createCookieFunctions<T>(Vue: typeof _Vue, key: string, isSessionCookie: boolean = false): CookieFunctions<T> {
  /**
   * cookie情報を取得する
   *
   * @returns cookie情報
   */
  const getCookie = function <T>(): T | null {
    return Vue.prototype.$cookies.get(key)
  }

  /**
   * cookie情報を登録する
   *
   * @param value cookie情報
   */
  const setCookie = async function <T>(value: T): Promise<void> {
    // 有効期間設定：セッションクッキーの場合は0、通常クッキーの場合は設定ファイルから保持時間を取得する
    const cookieExpireTime = isSessionCookie ? 0 : Function(`return (${process.env.VUE_APP_COOKIE_EXPIRE})`)()

    await Vue.prototype.$cookies.set(key, value, cookieExpireTime)
  }

  /**
   * cookie情報を削除する
   */
  const removeCookie = function (): void {
    Vue.prototype.$cookies.remove(key)
  }

  return {
    get: getCookie,
    set: setCookie,
    remove: removeCookie,
  }
}

declare module 'vue/types/vue' {
  /**
   * this.$appCookiesで呼び出すためのインターフェース定義
   */
  interface Vue {
    /** cookie情報 */
    $appCookies: {
      /** 認証情報 */
      auth: CookieFunctions<AppCookieTypes.Auth>
      /** 生徒情報(生徒ログイン時のみ有効) */
      authStudent: {
        /**
         * cookie情報を取得する
         *   未設定の場合は例外が発生する
         *
         * @returns cookie情報
         */
        get: () => AppCookieTypes.AuthStudent
        /**
         * cookie情報を登録する
         *
         * @param value cookie情報
         */
        set: (value: AppCookieTypes.AuthStudent) => Promise<void>
        /**
         * cookie情報を削除する
         */
        remove: () => void
      }
      /** 先生情報(先生ログイン時のみ有効) */
      authTeacher: {
        /**
         * cookie情報を取得する
         *   未設定の場合は例外が発生する
         *
         * @returns cookie情報
         */
        get: () => AppCookieTypes.AuthTeacher
        /**
         * cookie情報を登録する
         *
         * @param value cookie情報
         */
        set: (value: AppCookieTypes.AuthTeacher) => Promise<void>
        /**
         * cookie情報を削除する
         */
        remove: () => void
      }
      /** 塾管理者情報(塾管理者ログイン時のみ有効) */
      authAcademy: {
        /**
         * cookie情報を取得する
         *   未設定の場合は例外が発生する
         *
         * @returns cookie情報
         */
        get: () => AppCookieTypes.AuthAcademy
        /**
         * cookie情報を登録する
         *
         * @param value cookie情報
         */
        set: (value: AppCookieTypes.AuthAcademy) => Promise<void>
        /**
         * cookie情報を削除する
         */
        remove: () => void
      }
      /** システム管理者情報(システム管理者ログイン時のみ有効) */
      authAdmin: {
        /**
         * cookie情報を取得する
         *   未設定の場合は例外が発生する
         *
         * @returns cookie情報
         */
        get: () => AppCookieTypes.AuthAdmin
        /**
         * cookie情報を登録する
         *
         * @param value cookie情報
         */
        set: (value: AppCookieTypes.AuthAdmin) => Promise<void>
        /**
         * cookie情報を削除する
         */
        remove: () => void
      }
      /** 生徒メニューでの選択情報 */
      studentMenuState: {
        /** 教科コード */
        subjectCode: {
          /**
           * cookie情報を取得する
           *   未設定の場合はデフォルト値を設定してから返す
           *
           * @returns cookie情報
           */
          get: () => Promise<AppCookieTypes.StudentMenuState.SubjectCode>
          /**
           * cookie情報を登録する
           *
           * @param value cookie情報
           */
          set: (value: string) => Promise<void>
          /**
           * cookie情報を削除する
           */
          remove: () => void
        }
        /** 生徒メニューでの選択情報-学年コード */
        gradeCode: {
          /**
           * cookie情報を取得する
           *   未設定の場合はデフォルト値を設定してから返す
           *
           * @returns cookie情報
           */
          get: () => Promise<AppCookieTypes.StudentMenuState.GradeCode>
          /**
           * cookie情報を登録する
           *
           * @param value cookie情報
           */
          set: (value: string) => Promise<void>
          /**
           * cookie情報を削除する
           */
          remove: () => void
        }
      }
      /** 全Cookieの有効期間延長 */
      updateCookieExpire: () => Promise<void>
      /**
       * 認証情報系Cookieの削除
       */
      removeAuth: () => void
    }
  }
}
